import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, CircularProgress, IconButton, Typography, Chip } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { palette } from '../../../../../../theme/palette';
import { colCenter } from '@ui/style/style';
import { spaceBetween } from '../style';
import { ArrowRight, Edit, DocumentText } from 'iconsax-react';
import { DownloadAction } from '@ui/components/common/actions/DownloadAction';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { formatDisplayDate } from '@infrastructure/helpers';
import { ClientContractStatus, ClientContractStatusMapForAdmin } from '@application/common/enums/client-contract-status';
import UserContractApi from '@infrastructure/user-contract-api';
import { QGroupContractDialog } from './QgroupContractDialog';
import { useSearchParams } from 'react-router-dom';
import { getUserContractsByFreelancerAsync } from '@application/features/userContracts/userContractThunk';
const theme = palette('light');
export const QGroupContractInformation = () => {
    const [isOpen, setisOpen] = React.useState(false);
    const [selectedContractId, setSelectedContractId] = useState(undefined);
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('id');
    useEffect(() => {
        if (userId)
            dispatch(getUserContractsByFreelancerAsync(userId));
    }, [dispatch, userId]);
    const handleEdit = (contractId) => {
        setSelectedContractId(contractId);
        setisOpen(true);
    };
    return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(QGroupContractCard, { handleEdit: handleEdit }), _jsx(QGroupContractDialog, { handleClose: () => setisOpen(false), type: 'edit', open: isOpen, assigmentId: selectedContractId })] }));
};
export const QGroupContractCard = ({ handleEdit }) => {
    const { userContracts } = useAppSelector((state) => state.userContract);
    const [loadingStates, setLoadingStates] = useState({});
    const sortedContracts = useMemo(() => {
        const statusOrder = {
            [ClientContractStatus.Signed]: 0,
            [ClientContractStatus.Approved]: 1,
            [ClientContractStatus.PendingSignature]: 2,
            [ClientContractStatus.Reason]: 3,
            [ClientContractStatus.Rejected]: 4,
            [ClientContractStatus.Expired]: 5
        };
        return [...(userContracts ?? [])].sort((a, b) => {
            const aStatusRank = statusOrder[a.status] ?? Number.MAX_SAFE_INTEGER;
            const bStatusRank = statusOrder[b.status] ?? Number.MAX_SAFE_INTEGER;
            if (aStatusRank !== bStatusRank) {
                return aStatusRank - bStatusRank;
            }
            const aTime = new Date(a.contactStartDate ?? a.contactEndDate ?? 0).getTime();
            const bTime = new Date(b.contactStartDate ?? b.contactEndDate ?? 0).getTime();
            return bTime - aTime;
        });
    }, [userContracts]);
    const setIsLoading = (assignmentId, isLoading) => {
        setLoadingStates((prev) => ({ ...prev, [assignmentId]: isLoading }));
    };
    const getIsLoading = (assignmentId) => {
        return loadingStates[assignmentId] || false;
    };
    return (_jsx(_Fragment, { children: sortedContracts?.length ? (sortedContracts.map((contract, index) => (_jsx(Box, { sx: {
                background: 'white',
                borderRadius: '16px',
                padding: '20px'
            }, children: _jsxs(Box, { sx: { mb: 2 }, children: [_jsx(Box, { sx: { ...spaceBetween }, children: contract.status !== ClientContractStatus.Expired && (_jsx(IconButton, { onClick: () => {
                                handleEdit(contract.id);
                            }, children: _jsx(Edit, {}) })) }), _jsxs(Box, { sx: { ...colCenter, gap: 0.5 }, children: [_jsx(Typography, { variant: 'subtitle2', color: 'initial', children: formatDisplayDate(contract.contactStartDate) }), _jsx(ArrowRight, { size: '14px' }), _jsx(Typography, { variant: 'subtitle2', color: 'initial', children: formatDisplayDate(contract.contactEndDate) })] }), _jsxs(Typography, { variant: 'caption', color: 'text.secondary', sx: { display: 'block', mt: 1 }, children: ["Internal Department:", ' ', contract.internalDepartmentDisplayName
                                ? `${contract.internalDepartmentDisplayName}${contract.internalDepartmentCode ? ` (${contract.internalDepartmentCode})` : ''}`
                                : contract.internalDepartmentCode || 'Not recorded (legacy)'] }), (() => {
                        const docuSignContract = contract.clientContracts?.find((c) => c.autoGeneratedContractLink);
                        const shouldShowStatusButton = contract.status === ClientContractStatus.Signed ||
                            contract.status === ClientContractStatus.Approved ||
                            contract.status === ClientContractStatus.Expired ||
                            contract.status === ClientContractStatus.Rejected ||
                            contract.status === ClientContractStatus.PendingSignature;
                        if (shouldShowStatusButton) {
                            const hasLink = docuSignContract?.autoGeneratedContractLink &&
                                (contract.status === ClientContractStatus.Signed ||
                                    contract.status === ClientContractStatus.Approved);
                            const statusTag = ClientContractStatusMapForAdmin.get(contract.status);
                            if (hasLink) {
                                const isLoading = getIsLoading(contract.id);
                                return (_jsxs(Box, { mt: 1, position: 'relative', children: [_jsx(DownloadAction, { width: '100%', fileName: '', type: 'download', tag: statusTag, disabled: isLoading, onClick: async () => {
                                                setIsLoading(contract.id, true);
                                                try {
                                                    const response = await UserContractApi.getUserContractSigningUrl(contract.id);
                                                    if (response.data?.signingUrl) {
                                                        window.open(response.data.signingUrl, '_blank', 'noopener,noreferrer');
                                                    }
                                                    else {
                                                        window.open(docuSignContract.autoGeneratedContractLink, '_blank', 'noopener,noreferrer');
                                                    }
                                                }
                                                catch (error) {
                                                    window.open(docuSignContract.autoGeneratedContractLink, '_blank', 'noopener,noreferrer');
                                                }
                                                finally {
                                                    setIsLoading(contract.id, false);
                                                }
                                            } }), isLoading && (_jsx(Box, { sx: {
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                zIndex: 1
                                            }, children: _jsx(CircularProgress, { size: 20 }) }))] }));
                            }
                            return (_jsx(Box, { mt: 1, sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'black',
                                    background: theme.primary.light,
                                    borderRadius: '13px',
                                    width: '100%',
                                    position: 'relative',
                                    padding: '10px 15px',
                                    cursor: 'default'
                                }, children: _jsxs(Box, { display: 'flex', gap: 1, alignItems: 'center', children: [_jsx(Box, { sx: { ...colCenter }, children: _jsx(DocumentText, {}) }), _jsxs(Box, { sx: {
                                                textAlign: 'left',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flexGrow: 1
                                            }, children: [_jsx(Typography, { variant: 'caption', sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, color: 'inherit' }), _jsx(Box, { sx: { ...colCenter, gap: 1 }, children: statusTag && (_jsx(Chip, { label: statusTag, variant: 'soft', sx: { borderRadius: '20px' }, size: 'small' })) })] })] }) }));
                        }
                        return null;
                    })()] }) }, index)))) : (_jsx(Typography, { variant: 'body1', color: 'initial', children: "No contracts available." })) }));
};
